<template>
  <div v-if="ratings && ratings.length">
    <!-- Filters -->
    <section
      class="is-flex is-justify-content-space-around is-align-items-center"
    >
      <span class="is-size-5">Rating over time</span>
      <b-field class="my-0" label="Interval" label-position="on-border">
        <b-select v-model="interval">
          <option value="Day" key="Daily">Daily</option>
          <option value="Week" key="Weekly">Weekly</option>
          <option value="Month" key="Monthly">Monthly</option>
          <option value="Game" key="Game">Game</option>
        </b-select>
      </b-field>

      <b-field class="my-0" label="Period" label-position="on-border">
        <b-select v-model="period">
          <option value="1w" key="1w">last 7 days</option>
          <option value="2w" key="2w">last 2 weeks</option>
          <option value="1m" key="1m">last 30 days</option>
          <option value="3m" key="3m">last 90 days</option>
          <option value="6m" key="6m">last 6 months</option>
          <option value="YTD" key="YTD">YTD</option>
          <!-- <option value="1y" key="1y">1y</option> -->
          <option value="All" key="All">All time</option>
        </b-select>
      </b-field>
    </section>

    <div v-if="periodRatings && periodRatings.length">
      <GChart type="ComboChart" :data="chartData" :options="chartOptions" />
      <p><i>* Drag to zoom, right click to reset</i></p>
    </div>
    <div v-else>
      <b-notification type="is-warning">
        No game found for this period
      </b-notification>
    </div>
  </div>
</template>

<script>
import { GChart } from "vue-google-charts";
import { PlayersAPI } from "@/services/api";
import { startOf, startOfPeriod } from "vue-chess-shared/utils/date-helper";

export default {
  components: { GChart },
  name: "RatingChart",

  props: ["username", "course"],

  data() {
    return {
      isLoading: true,
      ratings: [],
      interval: "Day",
      period: "1m",
    };
  },

  computed: {
    filters() {
      return { chartInterval: this.interval, chartPeriod: this.period };
    },

    periodRatings() {
      let keyFn = null;
      switch (this.interval) {
        case "Day":
          keyFn = (t) => new Date(t).toDateString();
          break;
        case "Week":
          keyFn = (t) => startOf(new Date(t), "week").toDateString();
          break;
        case "Month":
          keyFn = (t) => startOf(new Date(t), "month").toDateString();
          break;
        default:
          break;
      }

      const periodStart = startOfPeriod(this.period);
      let periodRatings = periodStart
        ? this.ratings.filter((x) => x.created > periodStart)
        : this.ratings;

      periodRatings = Object.entries(
        periodRatings.reduce((rv, x, i) => {
          const key = this.interval === "Game" ? i + 1 : keyFn(x.created);
          rv[key] ? rv[key].push(x.rating) : (rv[key] = [x.rating]);
          return rv;
        }, {})
      );

      return periodRatings.map(([key, v], i) => [
        this.interval === "Game" ? key : new Date(key),
        {
          games: v.length,
          open: i
            ? periodRatings[i - 1][1][periodRatings[i - 1][1].length - 1]
            : v[0],
          close: v[v.length - 1],
          low: Math.min(...v),
          high: Math.max(...v),
          average: Math.round(v.reduce((p, c) => p + c, 0) / v.length),
        },
      ]);
    },

    chartData() {
      return [
        [
          "Key",
          "Rating by " + this.interval,
          "Open",
          "Close",
          "High",
          { label: "tooltip", type: "string", role: "tooltip" },
          "Average Rating",
          "Games",
        ],
        ...this.periodRatings.map(([k, v]) => [
          this.interval === "Game" ? k : new Date(k),
          v.low,
          v.open, //opening is last rating of previous interval
          v.close,
          v.high,
          `${v.open} → ${v.close}\n Range: [${v.low} - ${v.high}]\n Change: ${
            v.close > v.open ? "+" : ""
          }${v.close - v.open}`,
          v.average,
          v.games,
        ]),
      ];
    },

    chartOptions() {
      return {
        // title: "Rating over time",
        height: 500,
        chartArea: {
          width: "85%",
          height: "85%",
        },
        curveType: "function",
        vAxis: {
          title: "Rating",
          // minorGridlines: {
          //   color: "transparent",
          // },
        },
        hAxis: {
          title: this.interval,
          // minorGridlines: {
          //   color: "transparent",
          // },
        },
        seriesType: "line",
        series: {
          0: {
            type: "candlesticks",
            color: "green",
            strokeWidth: 10,
            backgroundColor: { strokeWidth: 5 },
            candlestick: {
              fallingColor: { strokeWidth: 0, fill: "#a52714" }, // red
              risingColor: { strokeWidth: 0, fill: "#0f9d58" }, // green
            },
          },
          1: { type: "line", color: "blue", displayAnnotations: false },
          2: { type: "bars", color: "orange", targetAxisIndex: 1 },
        },
        axes: {
          // Adds labels to each axis; they don't have to match the axis names.
          y: {
            Rating: { label: "Rating" },
            Games: { label: "Games" },
          },
        },
        vAxes: {
          0: {
            title: "Rating",
            textStyle: { color: "blue" },
            minValue:
              Math.min(...this.periodRatings.map((x) => x[1].low)) - 100,
          },
          1: {
            title: "Games",
            textStyle: { color: "orange" },
            minValue: this.interval === "Game" ? 1 : 0,
            maxValue:
              this.interval === "Game"
                ? 100
                : 3 * Math.max(...this.periodRatings.map((x) => x[1].games)),
          },
        },
        bar: { groupWidth: this.interval === "Game" ? 0 : "70%" }, // Remove space between bars.
        explorer: {
          axis: "horizontal",
          actions: ["dragToZoom", "rightClickToReset"],
          keepInBounds: true,
          maxZoomIn: this.periodRatings.length / 2,
          maxZoomOut: 1,
        },
        focusTarget: "category",
      };
    },
  },

  watch: {
    filters: function() {
      if (
        this.$route.query.chartInterval !== this.interval ||
        this.$route.query.chartPeriod !== this.period
      ) {
        const newQuery = { ...this.$route.query, ...this.filters };
        history.replaceState(
          {},
          null,
          "#" +
            this.$route.path +
            "?" +
            Object.keys(newQuery)
              .map((key) => {
                return (
                  encodeURIComponent(key) +
                  "=" +
                  encodeURIComponent(newQuery[key])
                );
              })
              .join("&")
        );
      }
    },
  },

  methods: {
    async queryRatingHistory() {
      this.isLoading = true;
      const data = await PlayersAPI.getPlayerCourseRatings(
        this.username,
        this.course
      );
      if (data) {
        this.ratings = data.sort((a, b) => a.created - b.created);
      }
      this.isLoading = false;
    },
  },

  created() {
    if (this.$route.query.chartInterval) {
      this.interval = this.$route.query.chartInterval;
    }
    if (this.$route.query.chartPeriod) {
      this.period = this.$route.query.chartPeriod;
    }

    this.queryRatingHistory();
  },
};
</script>

<style scoped></style>
